// Platform Config and Client APIs
import { apiRequest } from './request.js';
import { withCache } from './config.js';

// Platform Config API (Admin)
export const platformConfigAPI = {
    // Get platform configuration
    get: async () => {
        return apiRequest('/admin/platform-config/');
    },

    // Update platform configuration
    update: async (configData) => {
        return apiRequest('/admin/platform-config/', {
            method: 'PUT',
            body: JSON.stringify(configData)
        });
    },

    // Add family
    addFamily: async (entityType, code, name, description = '', technicalTypes = []) => {
        return apiRequest('/admin/platform-config/add-family/', {
            method: 'POST',
            body: JSON.stringify({
                entity_type: entityType,
                code,
                name,
                description,
                technical_types: technicalTypes
            })
        });
    },

    // Update family
    updateFamily: async (entityType, code, name, description = '', technicalTypes = []) => {
        return apiRequest('/admin/platform-config/update-family/', {
            method: 'PATCH',
            body: JSON.stringify({
                entity_type: entityType,
                code,
                name,
                description,
                technical_types: technicalTypes
            })
        });
    },

    // Remove family
    removeFamily: async (entityType, code) => {
        return apiRequest(`/admin/platform-config/remove-family/?entity_type=${encodeURIComponent(entityType)}&code=${encodeURIComponent(code)}`, {
            method: 'DELETE'
        });
    },

    // Add technical type
    addTechnicalType: async (code, name, description = '') => {
        return apiRequest('/admin/platform-config/add-technical-type/', {
            method: 'POST',
            body: JSON.stringify({ code, name, description })
        });
    },

    // Update technical type
    updateTechnicalType: async (code, name, description = '') => {
        return apiRequest('/admin/platform-config/update-technical-type/', {
            method: 'PATCH',
            body: JSON.stringify({ code, name, description })
        });
    },

    // Remove technical type
    removeTechnicalType: async (code) => {
        return apiRequest(`/admin/platform-config/remove-technical-type/?code=${encodeURIComponent(code)}`, {
            method: 'DELETE'
        });
    },

    // Get stats (usage statistics)
    getStats: async () => {
        return apiRequest('/admin/platform-config/stats/');
    },

    // Get feature toggles
    getFeatureToggles: async () => {
        return apiRequest('/admin/platform-config/feature-toggles/');
    },

    // Update single feature toggle
    updateFeatureToggle: async (key, value) => {
        return apiRequest('/admin/platform-config/update-feature-toggle/', {
            method: 'PUT',
            body: JSON.stringify({ key, value })
        });
    },

    // Update feature toggles in bulk
    updateFeatureTogglesBulk: async (toggles) => {
        return apiRequest('/admin/platform-config/update-feature-toggles-bulk/', {
            method: 'PUT',
            body: JSON.stringify({ toggles })
        });
    },

    // Get families
    getFamilies: async () => {
        return withCache('platform_families', () => apiRequest('/families/', {
            requireAuth: false
        }));
    },

    // Get technical types
    getTechnicalTypes: async () => {
        return withCache('platform_technical_types', () => apiRequest('/technical-types/', {
            requireAuth: false
        }));
    }
};

// Client API (for seeker role users) - Según API_DOC.md /api/client/
export const clientAPI = {
    // Get client dashboard stats
    getStats: async () => {
        return apiRequest('/client/stats/');
    },

    // Get client's reviews
    getReviews: async () => {
        return apiRequest('/client/reviews/');
    },

    // Get client's contacts (lead history)
    getContacts: async () => {
        return apiRequest('/client/contacts/');
    },

    // Get client's favorites
    getFavorites: async () => {
        return apiRequest('/client/favorites/');
    },

    // Get client profile
    getProfile: async () => {
        return apiRequest('/client/profile/');
    },

    // Update client profile
    updateProfile: async (profileData) => {
        return apiRequest('/client/profile/', {
            method: 'PUT',
            body: JSON.stringify(profileData)
        });
    },

    // Update client profile with multipart (avatar upload)
    updateProfileMultipart: async (formData) => {
        return apiRequest('/client/profile/', {
            method: 'PUT',
            body: formData
        });
    },

    // Alias for backwards compatibility
    getLeads: async () => {
        return clientAPI.getContacts();
    }
};
